import { useTurnstile } from "../TurnstileContext";
import { ViewType } from "../types";
import { Anchor, Button, Input, Message } from "./../base-ui/index";
import { magicLinkSignInMutationOptions } from "@/hooks/mutations/auth/magicLinkSignInMutationOptions";
import { VIEWS, I18nVariables, RedirectTo } from "@supabase/auth-ui-shared";
import { SupabaseClient } from "@supabase/supabase-js";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";

function MagicLink({
  setAuthView = () => {},
  supabaseClient,
  redirectTo,
  i18n,
  showLinks = false,
  skipBeforeUserCreateHookKey,
}: {
  setAuthView?: (view: ViewType) => void;
  supabaseClient: SupabaseClient;
  redirectTo?: RedirectTo;
  i18n?: I18nVariables;
  showLinks?: boolean;
  skipBeforeUserCreateHookKey?: string;
}) {
  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [emptyEmailError, setEmptyEmailError] = useState("");
  const { getToken } = useTurnstile();

  const { mutate, isPending, error, reset } = useMutation({
    ...magicLinkSignInMutationOptions({
      supabaseClient,
      getTurnstileToken: getToken,
      redirectTo,
      skipBeforeUserCreateHookKey,
    }),
    onSuccess: () =>
      setSuccessMessage(i18n?.magic_link?.confirmation_text as string),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuccessMessage("");
    setEmptyEmailError("");
    reset();

    if (email.length === 0) {
      setEmptyEmailError(i18n?.magic_link?.empty_email_address as string);
      return;
    }

    mutate({ email });
  };

  const labels = i18n?.magic_link;

  return (
    <form id="auth-magic-link" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-3 text-left">
        <Input
          id="email"
          name="email"
          type="email"
          autoFocus
          autoComplete="username"
          aria-label={labels?.email_input_label}
          placeholder={labels?.email_input_placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
        <Button color="primary" type="submit" loading={isPending}>
          {isPending ? labels?.loading_button_label : labels?.button_label}
        </Button>
        {showLinks && (
          <Anchor
            href="#auth-sign-in"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              setAuthView(VIEWS.SIGN_IN);
            }}
          >
            {i18n?.sign_in?.link_text}
          </Anchor>
        )}
        {successMessage && <Message>{successMessage}</Message>}
        {emptyEmailError && <Message color="danger">{emptyEmailError}</Message>}
        {error && <Message color="danger">{error.message}</Message>}
      </div>
    </form>
  );
}

export { MagicLink };
